/**
 * Phase 9 chunk 2 — pure-math GNSS constellation coloring decisions.
 *
 * Cesium-free so the decision math (`pickGnssStyle`, `buildMembershipIndex`)
 * lives in Vitest. PointPrimitiveLayer imports these helpers and applies
 * the returned style to the Cesium PointPrimitive whose payload type is
 * normally PAYLOAD (cyan).
 *
 * Locked palette (Galileo retuned post-Phase-11 per operator feedback —
 * the original soft `#5fa8d3` blue read too similarly to the default
 * cyan PAYLOAD color `#00d9ff`, so it didn't pop):
 *   - GPS     amber  `#ffb700`
 *   - Galileo violet `#a874f7`
 *   - GLONASS green  `#7cc282`
 *   - BeiDou  red-amber `#d77a3a`
 *
 * Constellation slugs use the friendly names the SPA cares about
 * (`gps`, `galileo`, `glonass`, `beidou`); the underlying CelesTrak
 * group slugs (`gps-ops`, `glo-ops`, etc.) only matter on the PHP
 * side — the `/api/v1/gnss/membership` endpoint already maps them.
 */

export type GnssConstellation = 'gps' | 'galileo' | 'glonass' | 'beidou';

export const GNSS_CONSTELLATIONS: readonly GnssConstellation[] = ['gps', 'galileo', 'glonass', 'beidou'] as const;

export const GNSS_COLORS: Record<GnssConstellation, string> = {
  gps:     '#ffb700',
  galileo: '#a874f7',
  glonass: '#7cc282',
  beidou:  '#d77a3a',
};

/** Slightly larger than the default 3 px PAYLOAD size so the GNSS pretzel pattern is legible. */
export const GNSS_PIXEL_SIZE = 4;

/**
 * Map shape returned by the `/api/v1/gnss/membership` endpoint.
 */
export interface GnssMembershipPayload {
  gps:     readonly number[];
  galileo: readonly number[];
  glonass: readonly number[];
  beidou:  readonly number[];
}

/**
 * Overlay flags driving whether each constellation is currently
 * painted. Default off (locked in § II row 4).
 */
export interface GnssOverlayFlags {
  gnssGps:     boolean;
  gnssGalileo: boolean;
  gnssGlonass: boolean;
  gnssBeidou:  boolean;
}

export const DEFAULT_GNSS_FLAGS: GnssOverlayFlags = {
  gnssGps:     false,
  gnssGalileo: false,
  gnssGlonass: false,
  gnssBeidou:  false,
};

/**
 * Builds a `norad → constellation` index from the API payload. NORADs
 * appearing in multiple constellations (shouldn't happen in practice
 * but is defensive against bad data) win toward the EARLIER entry
 * in {@link GNSS_CONSTELLATIONS}.
 */
export function buildMembershipIndex(payload: GnssMembershipPayload): Map<number, GnssConstellation> {
  const out = new Map<number, GnssConstellation>();
  for (const constellation of GNSS_CONSTELLATIONS) {
    const ids = payload[constellation] ?? [];
    for (const id of ids) {
      if (!out.has(id) && Number.isFinite(id)) {
        out.set(id, constellation);
      }
    }
  }
  return out;
}

/**
 * Decide whether a given NORAD should be styled with a GNSS color
 * given the current overlay flags. Returns null if the NORAD isn't a
 * GNSS member or its constellation's overlay is off — caller falls
 * back to the type-default styling.
 *
 * Pure math: no Cesium imports so it's Vitest-friendly.
 */
export function pickGnssStyle(
  norad: number,
  membership: Map<number, GnssConstellation>,
  flags: GnssOverlayFlags,
): { color: string; pixelSize: number } | null {
  const constellation = membership.get(norad);
  if (constellation === undefined) return null;
  const flagKey = (
    constellation === 'gps'     ? 'gnssGps'     :
    constellation === 'galileo' ? 'gnssGalileo' :
    constellation === 'glonass' ? 'gnssGlonass' :
                                  'gnssBeidou'
  ) as keyof GnssOverlayFlags;
  if (!flags[flagKey]) return null;
  return { color: GNSS_COLORS[constellation], pixelSize: GNSS_PIXEL_SIZE };
}

/** Returns true when at least one GNSS overlay is on. */
export function anyGnssEnabled(flags: GnssOverlayFlags): boolean {
  return flags.gnssGps || flags.gnssGalileo || flags.gnssGlonass || flags.gnssBeidou;
}
